import { css } from "@emotion/css";
import type { ProjectResource, EnvironmentResource } from "@octopusdeploy/octopus-server-client";
import { useMemo, useState } from "react";
import { TemplatesFiltersBar } from "~/areas/projects/components/Variables/TenantVariables/FiltersBar";
import { MissingValuesCallout } from "~/areas/projects/components/Variables/TenantVariables/MissingValuesCallout";
import { useScopedProjectTemplatesFilterState } from "~/areas/projects/components/Variables/TenantVariables/ScopedTenantVariables/useScopedProjectTemplatesFilterState";
import type { LoadedSourceItems } from "~/areas/projects/components/Variables/TenantVariables/useTenantVariableResourceOptions";
import type { DoBusyTask } from "~/components/DataBaseComponent/index";
import NumberedPagingBar from "../components/Paging/NumberedPagingBar";
import { type RowData, ScopedTenantTemplateDataGrid } from "./ScopedTenantTemplateDataGrid";
import type { ProjectVariableModelAction, TenantPageProjectVariableModel } from "./tenantVariablePageModel";
export interface ScopedProjectTemplateListProps {
    spaceId: string;
    variables: TenantPageProjectVariableModel[];
    projects: ProjectResource[];
    environments: EnvironmentResource[];
    variableSources: LoadedSourceItems;
    doBusyTask: DoBusyTask;
    onVariableChange: (action: ProjectVariableModelAction) => void;
}
export function ScopedProjectTemplateList(props: ScopedProjectTemplateListProps): JSX.Element {
    const { spaceId, variables, onVariableChange, doBusyTask, ...gridProps } = props;
    const { projects, environments } = gridProps;
    const templates = projects.flatMap((l) => l.Templates);
    const [filterState, setFilterState] = useScopedProjectTemplatesFilterState();
    const [pageSize, setPageSize] = useState<number>(30);
    const templateIdToIndex = templates.reduce((map: Record<string, number>, template, index) => {
        map[template.Id] = index;
        return map;
    }, {});
    const data = useMemo(() => variables
        .reduce((acc: RowData[], v) => {
        const template = templates.find((t) => t.Id === v.TemplateId);
        const project = projects.find((p) => p.Id === v.ProjectId);
        if (!template || !project)
            return acc; // This should never happen
        return [
            ...acc,
            {
                Id: v.Id ?? "",
                Template: template,
                Container: { Id: project.Id, Name: project.Name },
                Value: v.Value,
                Status: v.Status,
                EnvironmentIds: v.Scope.EnvironmentIds,
                IsTemplateChild: false, // Will be recalculated after filtering
                IsTenantChild: false,
                HasValueChanged: v.HasValueChanged,
                HasScopeChanged: v.HasScopeChanged,
            },
        ];
    }, [])
        .sort((a: RowData, b: RowData) => {
        const indexA = templateIdToIndex[a.Template.Id];
        const indexB = templateIdToIndex[b.Template.Id];
        if (indexA < indexB)
            return -1;
        if (indexA > indexB)
            return 1;
        return 0;
    }), [variables, templates, projects, templateIdToIndex]);
    const filteredData = useMemo(() => {
        const filtered = data
            .filter((r: RowData) => {
            if (!filterState.filterByProjectIds || filterState.filterByProjectIds.length === 0)
                return true;
            return filterState.filterByProjectIds.includes(r.Container.Id);
        })
            .filter((r: RowData) => {
            if (!filterState.filterByName)
                return true;
            return r.Template.Name.toLowerCase().includes(filterState.filterByName.toLowerCase());
        })
            .filter((r: RowData) => {
            switch (filterState.filterByValueStatus) {
                case "missing":
                    return r.Status === "missing" || r.Status === "new";
                case "overridden":
                    return r.Status === "overridden" || r.Status === "new";
                default:
                    return true;
            }
        })
            .filter((r: RowData) => {
            if (!filterState.filterByEnvironments || filterState.filterByEnvironments.length === 0)
                return true;
            return r.EnvironmentIds.length === 0 || filterState.filterByEnvironments.some((envId) => r.EnvironmentIds.includes(envId));
        });
        // Recalculate IsTemplateChild after filtering to ensure the first visible row shows the template name
        const visibleTemplates = new Set<string>();
        return filtered.map((row) => {
            const key = `${row.Template.Id}-${row.Container.Id}`;
            const isChild = visibleTemplates.has(key);
            visibleTemplates.add(key);
            return { ...row, IsTemplateChild: isChild };
        });
    }, [data, filterState]);
    const paginatedData = useMemo(() => filteredData.slice((filterState.pageNumber - 1) * pageSize, filterState.pageNumber * pageSize), [filteredData, filterState.pageNumber, pageSize]);
    const missingSetIds = new Set(variables.filter((v) => v.Status === "missing").map((v) => v.ProjectId));
    return (<>
            {missingSetIds.size !== 0 && <MissingValuesCallout filterState={filterState} setFilterState={setFilterState}/>}
            <TemplatesFiltersBar totalVariablesCount={data.length} filteredVariablesCount={filteredData.length} filterState={filterState} onFilterChange={setFilterState} filterData={{ tenants: null, environments: environments, tagSets: null }} projects={projects} unsavedChangesCount={0} doBusyTask={doBusyTask}/>
            <ScopedTenantTemplateDataGrid {...gridProps} data={paginatedData} enabledColumns={[
            { type: "Template", width: "30%" },
            { type: "Value", width: "30%" },
            { type: "Environments", width: "30%" },
            { type: "Actions", width: "10%" },
        ]} hasEditPermissions={true} onVariableValueUpdate={(variableId, value) => onVariableChange({ action: "updateValue", variableId, value: value ?? null })} onVariableScopeUpdate={(variableId, environmentIds) => onVariableChange({ action: "updateScope", variableId, environmentIds: environmentIds })} onVariableRemove={(variableId) => onVariableChange({ variableId, action: "remove" })} onVariableAdd={(_, projectId, templateId) => onVariableChange({ action: "addProjectVariable", projectId, templateId })} groupNames={projects.map((v) => v.Name)} doBusyTask={doBusyTask}/>

            <div className={listStyles.pagingBarStyles}>
                <NumberedPagingBar totalItems={filteredData.length} pageNumber={filterState.pageNumber} pageSize={pageSize} pageSizeOptions={[30, 50, 100]} onPagingSelectionChange={setPageState}/>
            </div>
        </>);
    function setPageState(pageNumber: number, newPageSize: number): void {
        setFilterState({ ...filterState, pageNumber: pageNumber });
        setPageSize(newPageSize);
    }
}
const listStyles = {
    pagingBarStyles: css({
        padding: "0 1rem",
    }),
};
